'use client';

import { Grid2, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { CampaignConfig } from '../config.interface';
import { Text } from '@/ui/Text';
import { DonateButton } from '@/ui/buttons/DonateButton';
import { KnowMoreButton } from '@/ui/buttons/KnowMoreButton';
import { useCorporateStore } from '@/infrastructure/zustand/corporateStoreProvider';
import AccreditedByLealtadImage from '@/public/sello-lealtad.png';
import Image from 'next/image';

export const CampaignDescription = ({ config }: { config: CampaignConfig }) => {
	const [trans] = useTranslation();
	const campaign = config.campaign;
	const campaignTexts = config.campaign?.texts;
	const themeBaseColor = useCorporateStore((state) => state.metadata.themeBaseColor);
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const isTablet = useMediaQuery(theme.breakpoints.down('md'));
	const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

	const sxcampaignInfoTitle = {
		fontSize: '20px',
		textAlign: 'left',
		color: themeBaseColor === '#ffffff' ? '#000000' : themeBaseColor,
		fontWeight: 900,
	};

	const sxdescription = {
		'textAlign': 'left',
		'& > p > a': {
			color: 'black',
		},
	};

	if (campaignTexts === undefined) {
		return null;
	}

	return (
		<Grid2 container direction="row" justifyContent="center" style={{ marginTop: '30px' }}>
			<Grid2 size={{ xs: 12 }}>
				{campaignTexts?.resume && (
					<Text
						disableColorDetection
						text={trans('description')}
						sx={sxcampaignInfoTitle}
						style={{ color: config.leftColumnDescriptionTitlesColor }}
					/>
				)}
				<Text disableColorDetection text={campaignTexts?.resume} sx={sxdescription} />
			</Grid2>
			{!config.disableTexts?.updatesAndLinks && (
				<Grid2 size={{ xs: 12 }}>
					{campaignTexts?.updateAndLinks && (
						<Text
							disableColorDetection
							text={trans('updatesAndLinks')}
							sx={sxcampaignInfoTitle}
							style={{ color: config.leftColumnDescriptionTitlesColor }}
						/>
					)}
					<Text disableColorDetection text={campaignTexts?.updateAndLinks} sx={sxdescription} />
					{config.campaign.ngos.some((ngo) => ngo.accreditedByLealtad) && (
						<Image
							// TODO: translate for accessibility
							alt="accredited by lealtad"
							src={AccreditedByLealtadImage.src}
							width={AccreditedByLealtadImage.width / 6}
							height={AccreditedByLealtadImage.height / 6}
							style={{ marginBottom: 16 }}
						/>
					)}
				</Grid2>
			)}
			{!config.disableTexts?.objectives && (
				<Grid2 size={{ xs: 12 }}>
					{campaignTexts?.objectives && (
						<Text
							disableColorDetection
							text={trans('campaignObjectives')}
							sx={sxcampaignInfoTitle}
							style={{ color: config.leftColumnDescriptionTitlesColor }}
						/>
					)}
					<Text disableColorDetection text={campaignTexts?.objectives} sx={sxdescription} />
				</Grid2>
			)}
			{!config.disableTexts?.activities && (
				<Grid2 size={{ xs: 12 }}>
					{campaignTexts?.activities && (
						<Text
							disableColorDetection
							text={trans('activities')}
							sx={sxcampaignInfoTitle}
							style={{ color: config.leftColumnDescriptionTitlesColor }}
						/>
					)}
					<Text disableColorDetection text={campaignTexts?.activities} sx={sxdescription} />
				</Grid2>
			)}
			{!config.disableTexts?.whoWillBenefit && (
				<Grid2 size={{ xs: 12 }}>
					{campaignTexts?.benefits && (
						<Text
							disableColorDetection
							text={trans('whoWillBenefit')}
							sx={sxcampaignInfoTitle}
							style={{ color: config.leftColumnDescriptionTitlesColor }}
						/>
					)}
					<Text disableColorDetection text={campaignTexts?.benefits} sx={sxdescription} />
				</Grid2>
			)}
			<Grid2
				container
				sx={
					config.isAlala && isTablet && !isDesktop && !isMobile
						? { display: 'none' }
						: { display: 'flex', flexDirection: 'column' }
				}
				direction={'column'}
				alignItems={'center'}
			>
				{!campaign.disabled && !config.isInformativeCampaign && (
					<DonateButton
						config={config}
						color={config.buttonColor}
						isFunded={campaign.finished}
						campaignId={campaign.id}
						style={{ marginBottom: '1rem' }}
					/>
				)}
				{config.knowMoreButton && <KnowMoreButton />}
			</Grid2>
		</Grid2>
	);
};
